export const SUPPORTED_LOCALES = Object.freeze(["fr", "en"]);
export const DEFAULT_LOCALE = "fr";
export const LOCALE_STORAGE_KEY = "janus.locale";
export const LOCALE_CHANGE_EVENT = "janus:localechange";

const localeTags = Object.freeze({ fr: "fr-FR", en: "en-US" });
const listeners = new Set();
const catalogs = { fr: Object.create(null), en: Object.create(null) };

export function normalizeLocale(value, fallback = DEFAULT_LOCALE) {
  const candidate = String(value || "")
    .trim()
    .toLowerCase()
    .replace("_", "-")
    .split("-")[0];
  return SUPPORTED_LOCALES.includes(candidate) ? candidate : fallback;
}

export function detectLocale() {
  if (typeof window !== "undefined") {
    try {
      const saved = window.localStorage?.getItem(LOCALE_STORAGE_KEY);
      if (saved && SUPPORTED_LOCALES.includes(normalizeLocale(saved, ""))) {
        return normalizeLocale(saved);
      }
    } catch {
      // Storage can be disabled by the browser. Language selection still works in memory.
    }
  }

  const browserLocales = typeof navigator !== "undefined"
    ? [...(Array.isArray(navigator.languages) ? navigator.languages : []), navigator.language]
    : [];
  for (const value of browserLocales) {
    const locale = normalizeLocale(value, "");
    if (locale) return locale;
  }
  return DEFAULT_LOCALE;
}

let activeLocale = detectLocale();

function applyDocumentLocale(locale) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
  const manifest = document.querySelector('link[rel="manifest"]');
  if (manifest) manifest.setAttribute("href", locale === "en" ? "/manifest.en.webmanifest" : "/manifest.webmanifest");
}

function notify(locale) {
  for (const listener of [...listeners]) listener(locale);
}

export function getLocale() {
  return activeLocale;
}

export function getLocaleTag(locale = activeLocale) {
  return localeTags[normalizeLocale(locale)] || localeTags[DEFAULT_LOCALE];
}

export function setLocale(value, options = {}) {
  const locale = normalizeLocale(value, activeLocale || DEFAULT_LOCALE);
  const changed = locale !== activeLocale;
  activeLocale = locale;
  applyDocumentLocale(locale);

  if (options.persist !== false && typeof window !== "undefined") {
    try {
      window.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Keep the in-memory preference if persistence is unavailable.
    }
  }

  if (changed || options.force === true) {
    notify(locale);
    if (options.broadcast !== false && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(LOCALE_CHANGE_EVENT, { detail: { locale } }));
    }
  }
  return locale;
}

export function subscribe(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function registerMessages(locale, messages) {
  const normalized = normalizeLocale(locale, "");
  if (!normalized || !messages || typeof messages !== "object") return;
  Object.assign(catalogs[normalized], messages);
}

export function getRegisteredMessages(locale = activeLocale) {
  const normalized = normalizeLocale(locale);
  return Object.freeze({ ...(catalogs[normalized] || {}) });
}

function interpolate(message, params) {
  if (!params || typeof params !== "object") return message;
  return message.replace(/\{([a-zA-Z0-9_.-]+)\}/g, (placeholder, name) => {
    if (!Object.prototype.hasOwnProperty.call(params, name)) return placeholder;
    const value = params[name];
    return value === null || value === undefined ? "" : String(value);
  });
}

export function translate(key, params = {}, locale = activeLocale) {
  const normalized = normalizeLocale(locale);
  const message = catalogs[normalized]?.[key]
    ?? catalogs[DEFAULT_LOCALE]?.[key]
    ?? catalogs.en?.[key]
    ?? key;
  return interpolate(String(message), params);
}

if (typeof window !== "undefined") {
  applyDocumentLocale(activeLocale);
  window.addEventListener(LOCALE_CHANGE_EVENT, (event) => {
    const locale = normalizeLocale(event.detail?.locale, "");
    if (locale && locale !== activeLocale) {
      setLocale(locale, { persist: false, broadcast: false });
    }
  });
  window.addEventListener("storage", (event) => {
    if (event.key !== LOCALE_STORAGE_KEY || !event.newValue) return;
    setLocale(event.newValue, { persist: false, broadcast: false });
  });
}
